import React, { useState, useCallback } from 'react';
import { TwoCol, FieldLabel, RadioRow, RadioCard, Secondary, Primary, SectionSubtext, TemplateInputHeader } from './shared';
import StyledInput from 'src/components/BeaconV2Components/common/StyledInput';
import EstimatedCostCard from './components/EstimatedCostCard';
import RetailersSummary from './components/RetailersSummary';
import ActiveRetailerMatchBar from './components/ActiveRetailerMatchBar';
import { groupUrlsByRetailerId, RetailerPattern } from './utils/retailers';
import UrlListTable from './components/UrlListTable';
import ShelfBulkUploadModal from './components/ShelfBulkUploadModal';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import { lowerCase } from 'lodash';
import CoverageSnackbar from './components/CoverageSnackbar';
import { useUserEmail } from 'src/utils/Hooks/reduxSelectors';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks/typedHooks';


export interface InputsViewProps {
  onBack: () => void;
  onNext: (urls: string[]) => void;
  onRetailerCountChange?: (count: number) => void;
  onProjectNameChange?: (name: string) => void;
  onUrlsByRetailerIdChange?: (map: Record<string, string[]>) => void;
  templateType?: string;
}

const urlRegex = /^(https?:\/\/).+/i;
const domainLikeRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+(?:\/.*)?$/i;

const getUniqueUrlsFromMap = (map: Record<string, string[]>): string[] => {
  const list = Object.values(map).flat();
  return Array.from(new Set(list));
};

const InputsView: React.FC<InputsViewProps> = ({ onBack, onNext, onRetailerCountChange, onProjectNameChange, onUrlsByRetailerIdChange, templateType }) => {
  const [showBulk, setShowBulk] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);
  const [validUrls, setValidUrls] = useState<string[]>([]);
  const [hasMatchingRetailer, setHasMatchingRetailer] = useState<boolean>(false);
  const [retailerCount, setRetailerCount] = useState<number>(0);
  const [retailerIds, setRetailerIds] = useState<string[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const userEmail = useUserEmail() || '';
  const divisionId = useAppSelector((state) => (state as any)?.user?.session?.divisionId ?? null);
  const { showSnackbar } = useSnackbar();

  const [hoverBulk, setHoverBulk] = useState(false);
  const [hoverBack, setHoverBack] = useState(false);


  const canProceed = projectName.trim().length > 0 && hasMatchingRetailer && validUrls.length > 0;
  const disabledTooltip = canProceed ? '' : 'Enter a project name and at least one valid URL with a matching retailer to proceed to the next step';

  const handleUrlsByRetailerMapChange = useCallback((map: Record<string, string[]>) => {
    const unique = getUniqueUrlsFromMap(map);
    setValidUrls(unique);
    setHasMatchingRetailer(Object.keys(map).length > 0);
    setRetailerIds(Object.keys(map));
    onUrlsByRetailerIdChange && onUrlsByRetailerIdChange(map);
  }, [onUrlsByRetailerIdChange]);

  const rowsPerPage = 5;

  const [retailerNameById, setRetailerNameById] = useState<Record<string, string>>({});
  const [activeRetailerIdSet, setActiveRetailerIdSet] = useState<Set<string>>(new Set());
  const [retailerPatterns, setRetailerPatterns] = useState<RetailerPattern[]>([]);

  // Fetch active retailers for the selected template type and build matching patterns (websiteURL)
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const typeParam = (templateType || 'pricing').toString().toLowerCase();
        const retailersPath = buildLlmCrawlerPath('retailers');
        const templatesPath = buildLlmCrawlerPath('active-templates/retailers');
        const [{ data: retailersRes }, { data: activeRes }] = await Promise.all([
          axios.get(retailersPath),
          axios.get(templatesPath, { params: { templateType: lowerCase(typeParam), env: 'dev' } })
        ]);
        const activeArr = Array.isArray(activeRes?.activeRetailerIds)
          ? activeRes.activeRetailerIds
          : Array.isArray(activeRes?.retailerIds)
            ? activeRes.retailerIds
            : Array.isArray(activeRes?.items)
              ? activeRes.items
              : Array.isArray(activeRes)
                ? activeRes
                : [];
        const activeSet = new Set<string>((activeArr as any[]).map((v) => String(typeof v === 'object' ? v?.retailerId ?? v?.id ?? v : v)));
        if (mounted) setActiveRetailerIdSet(activeSet);

        const retailerList = Array.isArray(retailersRes?.retailers) ? retailersRes.retailers : [];
        // Build name map here to avoid separate API call
        const nameMap = retailerList.reduce((acc: Record<string, string>, r: any) => {
          const id = String(r?.retailerId ?? r?.id ?? '');
          const name = r?.retailerName || r?.name || '';
          if (id) acc[id] = name;
          return acc;
        }, {});
        if (mounted) setRetailerNameById(nameMap);
        const patterns: RetailerPattern[] = retailerList
          .map((r: any) => {
            const id = String(r?.retailerId ?? r?.id ?? '');
            const candidates: string[] = [r?.url].filter((s: any) => typeof s === 'string' && s) as string[];
            return { id, candidates } as RetailerPattern;
          })
          .filter((p) => p.id && p.candidates.length > 0 && activeSet.has(p.id));
        if (mounted) setRetailerPatterns(patterns);

        try {
          const activeRetailersDetailed = retailerList
            .filter((r: any) => activeSet.has(String(r?.retailerId ?? r?.id ?? '')))
            .map((r: any) => ({
              id: String(r?.retailerId ?? r?.id ?? ''),
              name: r?.retailerName || r?.name || '',
              url: r?.url
            }));
          // eslint-disable-next-line no-console
          console.log('[Agent Creator] Active retailers:', activeRetailersDetailed);
        } catch { }
      } catch (_e) {
        if (mounted) setActiveRetailerIdSet(new Set());
        if (mounted) setRetailerPatterns([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [templateType]);

  const normalizeCandidate = (raw: string): string | null => {
    const s = raw.trim();
    if (!s || /^retry$/i.test(s)) return null;
    if (urlRegex.test(s)) return s;
    if (domainLikeRegex.test(s)) return `https://${s}`;
    return null;
  };

  const parseAndStore = (candidates: string[]) => {
    const normalized = candidates
      .map(normalizeCandidate)
      .filter((v): v is string => Boolean(v));
    setUrls((prev) => Array.from(new Set([...prev, ...normalized])));
  };


  const removeUrl = (value: string) => {
    setUrls((prev) => prev.filter((u) => u !== value));
  };

  // Recompute retailer IDs and validity whenever URLs change (using known retailer patterns and active set)
  React.useEffect(() => {
    const unique = Array.from(new Set(urls));
    const byRetailer = groupUrlsByRetailerId(unique, retailerPatterns);
    const rids = Object.keys(byRetailer);
    // Only count/show retailers that are currently active
    const activeRids = rids.filter((rid) => activeRetailerIdSet.has(rid));
    setHasMatchingRetailer(activeRids.length > 0);
    setRetailerIds(activeRids);
    setRetailerCount(activeRids.length);
    if (onRetailerCountChange) onRetailerCountChange(rids.length);
    // Only URLs that map to an active retailer are considered valid (green)
    const valid = unique.filter((u) => {
      const ridMap = groupUrlsByRetailerId([u], retailerPatterns);
      const rid = Object.keys(ridMap)[0];
      return rid ? activeRetailerIdSet.has(rid) : false;
    });
    setValidUrls(valid);
    onUrlsByRetailerIdChange && onUrlsByRetailerIdChange(byRetailer);
  }, [urls, onUrlsByRetailerIdChange, activeRetailerIdSet, onRetailerCountChange]);

  const getUnknownUrls = React.useCallback((): string[] => {
    const unique = Array.from(new Set(urls));
    return unique.filter((u) => {
      const ridMap = groupUrlsByRetailerId([u], retailerPatterns);
      const rid = Object.keys(ridMap)[0];
      if (!rid) return true; // no retailer match
      return !activeRetailerIdSet.has(rid); // matched retailer but not active
    });
  }, [urls, activeRetailerIdSet, retailerPatterns]);

  const hasUnknown = getUnknownUrls().length > 0;



  const tryAddInlineUrl = () => {
    const candidate = urlInput.trim();
    if (candidate.length === 0) return;
    if (!urlRegex.test(candidate)) {
      setUrlError('Please enter a valid URL starting with http(s)://');
      return;
    }
    setUrls((prev) => (prev.includes(candidate) ? prev : [...prev, candidate]));
    setUrlInput('');
    setUrlError(null);
  };

  return (
    <div>
      <TwoCol style={{ gap: 45, width: 1045 }}>
        <div>
          <TemplateInputHeader>Project Name</TemplateInputHeader>
          <StyledInput
            placeholder="Enter a name for this data collection project"
            value={projectName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              setProjectName(val);
              onProjectNameChange && onProjectNameChange(val);
            }}
            style={{ width: '100%' }}
          />

          <div style={{ height: 16 }} />
          <TemplateInputHeader style={{ marginBottom: 20 }}>Input Type</TemplateInputHeader>
          <RadioRow style={{ columnGap: 48 }}>
            <RadioCard selected style={{ border: 'none', background: 'transparent', padding: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" name="inputType" defaultChecked style={{ accentColor: '#031425' }} />
                <span style={{ fontWeight: 700, fontSize: 16, color: '#031425' }}>Product URLs</span>
              </div>
              <SectionSubtext style={{ marginTop: 8, marginBottom: 0, fontSize: 14, lineHeight: '20px', color: '#031425', marginLeft: 24, fontWeight: 400 }}>
                Select individual products to get pricing on. Use this option to save cost or when you know exactly the
                SKUs you need data for.
              </SectionSubtext>
            </RadioCard>

            <RadioCard style={{ border: 'none', background: 'transparent', padding: 0, opacity: 0.55, cursor: 'not-allowed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="radio" name="inputType" disabled style={{ accentColor: '#C9D6E2' }} />
                <span style={{ fontWeight: 700, fontSize: 16, color: '#031425' }}>Search Terms (Coming soon)</span>
              </div>
              <SectionSubtext style={{ marginTop: 8, marginBottom: 0, fontSize: 14, lineHeight: '20px', color: '#9AA9BD', marginLeft: 24 }}>
                Select search terms that Stackline will search on the retailers chosen. Use this option when you have a
                broad catalog or aren’t sure exactly what products you need.
              </SectionSubtext>
            </RadioCard>
          </RadioRow>

          <div style={{ height: 16 }} />
          <FieldLabel style={{ fontSize: 16 }}>Product URLs</FieldLabel>
          {urls.length > 0 && (
            <UrlListTable urls={urls} validUrls={validUrls} rowsPerPage={rowsPerPage} onRemoveUrl={removeUrl} />
          )}
          <div style={{ height: 8 }} />
          <StyledInput
            placeholder="Enter a valid Product URL"
            value={urlInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setUrlInput(e.target.value);
              if (urlError) setUrlError(null);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                tryAddInlineUrl();
              }
            }}
            style={{ width: '100%' }}
          />
          {urlError ? <div style={{ color: '#B42318', marginTop: 6, fontSize: 12 }}>{urlError}</div> : null}
          <div style={{ height: 8 }} />

          <Secondary
            onClick={() => setShowBulk(true)}
            onMouseEnter={() => setHoverBulk(true)}
            onMouseLeave={() => setHoverBulk(false)}
            style={{
              borderRadius: 4,
              background: '#F8F9F9',
              transition: 'background-color 120ms ease',
              backgroundColor: hoverBulk ? '#F0F2F5' : '#F8F9F9',
              marginTop: 16,
              fontWeight: 500,
              color: '#031425'
            }}
          >
            Bulk Upload
          </Secondary>
          {showBulk && (
            <ShelfBulkUploadModal
              open={showBulk}
              onClose={() => setShowBulk(false)}
              onSave={(items) => {
                parseAndStore(items);
                setShowBulk(false);
              }}
            />
          )}


          <div className="inputs-retailers-summary" style={{ marginTop: 16 }}>
            <RetailersSummary retailerIds={retailerIds} retailerNameById={retailerNameById} />
            <ActiveRetailerMatchBar urls={urls} activeRetailerIdSet={activeRetailerIdSet} retailerPatterns={retailerPatterns} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <Secondary
              onClick={onBack}
              onMouseEnter={() => setHoverBack(true)}
              onMouseLeave={() => setHoverBack(false)}
              style={{
                borderRadius: 4,
                border: '1px solid #E8E8ED',
                backgroundColor: hoverBack ? '#F8F9F9' : '#FFF',
                transition: 'background-color 120ms ease'
              }}
            >
              Back
            </Secondary>
            <span title={disabledTooltip} style={{ display: 'inline-block' }}>
              <Primary
                onClick={() => onNext(validUrls)}
                disabled={!canProceed}
                style={{ opacity: canProceed ? 1 : 0.5, cursor: canProceed ? 'pointer' : 'not-allowed' }}
              >
                Next
              </Primary>
            </span>
          </div>

          {hasUnknown && (
            <div style={{ marginTop: 16 }}>
              <CoverageSnackbar
                onReachOut={async () => {
                  const bad = getUnknownUrls();
                  const emailHeader = 'Retailer Coverage Request';
                  const emailBody = `Requester: ${userEmail || 'unknown'}\nDivisionId: ${divisionId ?? 'unknown'}\n\nThe following URLs are not mapped to supported retailers.\n\n${bad.join('\n')}`;
                  try {
                    const path = buildLlmCrawlerPath('email/send');
                    await axios.post(
                      path,
                      {
                        emailBody,
                        title: emailHeader,
                        emailTitle: emailHeader,
                        userEmail,
                        divisionId
                      },
                      { params: { env: 'dev' } }
                    );
                    showSnackbar({ type: 'success', message: "Thanks for emailing our team, we'll get back to you shortly" });
                  } catch (e) {
                    showSnackbar({ type: 'error', message: 'Failed to send email. Please try again.' });
                  }
                }}
              />
            </div>
          )}
        </div>

        <div>
          <EstimatedCostCard productUrls={validUrls.length} retailers={retailerCount} frequency={'-'} />
        </div>
      </TwoCol>
    </div>
  );
};

export default InputsView;
